import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, FileText, Lock, Minus, PenLine, Plus } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { homePathForUser } from "@/lib/roleHome";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
const NDA_PDF_URL = `${API_BASE}/auth/legal/nda.pdf`;

function StepBadge({ step, label, active, done }: { step: number; label: string; active: boolean; done: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors",
          done
            ? "bg-emerald-500 text-white"
            : active
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground"
        )}
      >
        {done ? <CheckCircle2 className="h-4 w-4" /> : step}
      </div>
      <span
        className={cn(
          "text-sm font-medium",
          active ? "text-foreground" : "text-muted-foreground"
        )}
      >
        {label}
      </span>
    </div>
  );
}

export default function LegalOnboardingPage() {
  const { user, loading, getLegalDocuments, acceptTerms, signNda } = useAuth();
  const [legalLoading, setLegalLoading] = useState(false);
  const [termsText, setTermsText] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const observerRef = useRef<ResizeObserver | null>(null);
  const toast = useToast();
  const navigate = useNavigate();

  // Callback ref: attaches a ResizeObserver exactly when the PDF viewer mounts
  // (i.e. after the loading spinner clears), keeping pages sized to the container.
  const setPdfContainer = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    const measure = () => setContainerWidth(node.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  // Fit-to-width as the base, then apply the user's zoom factor.
  const pageWidth = Math.max(280, (containerWidth - 24) * zoom);
  // US-Letter aspect ratio (11 / 8.5) — used to size loading placeholders so
  // pages reserve their space and show a spinner instead of blank white.
  const pageHeight = pageWidth * 1.294;

  const needsTerms = Boolean(user?.needs_terms_acceptance);
  const needsNda = Boolean(user?.needs_nda_signature);

  const blockerTitle = useMemo(() => {
    if (needsTerms && needsNda) return "Complete Legal Onboarding";
    if (needsTerms) return "Terms Update Required";
    return "NDA Signature Required";
  }, [needsTerms, needsNda]);

  const blockerSubtitle = useMemo(() => {
    if (needsTerms && needsNda)
      return "Please review and accept the Terms & Conditions, then sign the NDA to unlock your account.";
    if (needsTerms)
      return "Our terms have been updated. Please review and accept to continue using the platform.";
    return "A Non-Disclosure Agreement is required before accessing the marketplace.";
  }, [needsTerms, needsNda]);

  useEffect(() => {
    if (!user) return;
    if (!needsTerms && !needsNda) {
      navigate(homePathForUser(user), { replace: true });
      return;
    }

    let cancelled = false;
    setLegalLoading(true);
    getLegalDocuments()
      .then((docs) => {
        if (cancelled) return;
        setTermsText(docs.terms_text);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : "Unable to load legal documents");
        }
      })
      .finally(() => {
        if (!cancelled) setLegalLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, needsTerms, needsNda, getLegalDocuments, navigate, toast]);

  const handleAcceptTerms = async () => {
    if (!consentAccepted) {
      toast.error("Please accept the Terms and Conditions to continue");
      return;
    }

    setSubmitting(true);
    try {
      await acceptTerms({ consent_accepted: true });
      toast.success("Terms accepted successfully");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Unable to accept terms");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignNda = async () => {
    if (signatureName.trim().length < 3) {
      toast.error("Please type your full legal name as digital signature");
      return;
    }

    setSubmitting(true);
    try {
      const updated = await signNda({ nda_signature_name: signatureName.trim() });
      toast.success("NDA signed successfully");
      // Land on the role-appropriate home (the legal-complete effect would also
      // redirect here once `user` refreshes; navigating now avoids the flash).
      const target = updated ?? user;
      if (target) navigate(homePathForUser(target), { replace: true });
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Unable to sign NDA");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || legalLoading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-background py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    navigate("/sign-in", { replace: true });
    return null;
  }

  const termsStep = 1;
  const ndaStep = 2;
  const termsDone = !needsTerms;
  const ndaDone = !needsNda;

  return (
    <main className="flex-1 bg-muted/40">
      {/* Hero */}
      <div className="bg-background border-b border-border/80">
        <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex items-start gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Lock className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">{blockerTitle}</h1>
              <p className="mt-1.5 text-base text-muted-foreground leading-relaxed">{blockerSubtitle}</p>
            </div>
          </div>

          {/* Progress steps — the NDA step only exists for roles that must sign
              one (buyers are exempt and just accept the terms). */}
          {needsTerms || needsNda ? (
            <div className="mt-8 flex items-center gap-3">
              <StepBadge step={termsStep} label="Terms & Conditions" active={needsTerms} done={termsDone} />
              {needsNda && (
                <>
                  <div className={cn("h-px flex-1 transition-colors", termsDone ? "bg-emerald-400" : "bg-border")} />
                  <StepBadge step={ndaStep} label="Non-Disclosure Agreement" active={!needsTerms && needsNda} done={ndaDone} />
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8 space-y-6">
        {/* Terms Card */}
        {needsTerms && (
          <Card className="rounded-2xl border-border/80 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Terms and Conditions</CardTitle>
                  <CardDescription className="text-sm">
                    Review and accept the latest platform terms to proceed.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Inline T&C content */}
              <div className="h-72 overflow-y-auto rounded-xl border border-border/70 bg-muted/30 px-5 py-4 text-sm leading-7 text-muted-foreground [&_a]:pointer-events-none [&_a]:text-inherit [&_a]:no-underline">
                {termsText ? (
                  termsText.split(/\n{2,}/).filter(Boolean).map((para, i) => {
                    const isHeading = /^\d+\.\s/.test(para) && para.length < 55;
                    return isHeading
                      ? <p key={i} className="mt-4 first:mt-0 font-semibold text-foreground">{para}</p>
                      : <p key={i} className="mt-2 first:mt-0">{para}</p>;
                  })
                ) : (
                  <p className="text-muted-foreground">Loading terms…</p>
                )}
              </div>

              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/70 bg-muted/40 p-4 transition-colors hover:bg-muted/60">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
                  checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                />
                <span className="text-sm text-foreground leading-relaxed">
                  I have read and agree to the latest Terms and Conditions.
                </span>
              </label>

              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-muted-foreground">
                  {needsNda
                    ? "After confirming, you'll be prompted to sign the NDA."
                    : "After confirming, you'll be taken to the marketplace."}
                </p>
                <Button onClick={handleAcceptTerms} disabled={submitting || !consentAccepted} className="gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Confirm Terms
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* NDA Card */}
        {!needsTerms && needsNda && (
          <Card className="rounded-2xl border-border/80 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                  <PenLine className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Non-Disclosure Agreement</CardTitle>
                  <CardDescription className="text-sm">
                    Read the NDA below, then provide your digital signature to continue.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/30">
                {/* Zoom toolbar */}
                <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-muted/50 px-3 py-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    {numPages > 0 ? `${numPages} page${numPages === 1 ? "" : "s"}` : "Document"}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                      disabled={zoom <= 0.5}
                      aria-label="Zoom out"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-12 text-center text-xs font-semibold tabular-nums text-foreground">
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      type="button"
                      onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}
                      disabled={zoom >= 2.5}
                      aria-label="Zoom in"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div
                  ref={setPdfContainer}
                  className="h-[32rem] overflow-auto bg-muted/20 p-3"
                >
                  <Document
                    file={NDA_PDF_URL}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    loading={
                      <div className="flex h-[28rem] flex-col items-center justify-center gap-3 text-muted-foreground">
                        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                        <p className="text-sm">Loading NDA…</p>
                      </div>
                    }
                    error={<p className="px-2 py-4 text-sm text-destructive">Unable to load the NDA document. Please refresh and try again.</p>}
                    className="mx-auto flex w-max min-w-full flex-col items-center gap-3"
                  >
                    {containerWidth > 0 &&
                      Array.from({ length: numPages }, (_, i) => (
                        <Page
                          key={i}
                          pageNumber={i + 1}
                          width={pageWidth}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading={
                            <div
                              style={{ width: pageWidth, height: pageHeight }}
                              className="flex items-center justify-center rounded-md bg-card shadow-sm ring-1 ring-border/40"
                            >
                              <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                            </div>
                          }
                          className="overflow-hidden rounded-md shadow-sm ring-1 ring-border/40"
                        />
                      ))}
                  </Document>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ndaSignatureName" className="text-sm font-medium">
                  Digital Signature — type your full legal name
                </Label>
                <Input
                  id="ndaSignatureName"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="e.g. Jane A. Smith"
                  className="font-medium"
                />
                <p className="text-xs text-muted-foreground">
                  By typing your name, you are providing a legally binding digital signature.
                </p>
              </div>

              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-muted-foreground">
                  Signing grants you full access to the marketplace.
                </p>
                <Button
                  onClick={handleSignNda}
                  disabled={submitting || signatureName.trim().length < 3}
                  className="gap-2"
                >
                  <PenLine className="h-4 w-4" />
                  Sign & Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* All done state */}
        {!needsTerms && !needsNda && (
          <Card className="rounded-2xl border-border/80 shadow-sm">
            <CardContent className="py-10 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
              <p className="mt-3 text-base font-medium text-foreground">All legal requirements are complete.</p>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Access to core portal features remains blocked until legal verification is complete.
          {" "}Need help?{" "}
          <Link to="/support" className="font-medium text-primary underline-offset-2 hover:underline">
            Contact Support
          </Link>
          .
        </p>
      </div>
    </main>
  );
}
